import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { 
  ShoppingCart, AlertCircle, CheckCircle2, Clock, Send, 
  TrendingUp, RefreshCw, Settings, Loader2, Phone, Euro,
  MessageSquare, Bell, History, Target, Zap
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export default function AbandonedOrdersPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [filter, setFilter] = useState("all");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, ordersRes, configRes] = await Promise.all([
        authAxiosRef.current.get("/abandoned-orders/dashboard"),
        authAxiosRef.current.get("/abandoned-orders/list?limit=50"),
        authAxiosRef.current.get("/abandoned-orders/config")
      ]);
      setDashboard(dashRes.data);
      setOrders(ordersRes.data.orders || []);
      setConfig(configRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar datos");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await authAxios.put("/abandoned-orders/config", config);
      toast.success("Configuración guardada");
      setShowConfig(false);
      fetchData();
    } catch (err) {
      toast.error("Error al guardar");
    }
    setSaving(false);
  };

  const handleSendReminder = async (linkId) => {
    setSaving(true);
    try {
      await authAxios.post(`/abandoned-orders/send-reminder/${linkId}`);
      toast.success("Recordatorio enviado");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar recordatorio");
    }
    setSaving(false);
  };

  const handleMarkPaid = async (linkId) => {
    setSaving(true);
    try {
      const res = await authAxios.post(`/abandoned-orders/mark-paid/${linkId}`);
      toast.success(res.data.recovered ? "¡Pedido recuperado!" : "Pedido marcado como pagado");
      fetchData();
    } catch (err) {
      toast.error("Error al marcar como pagado");
    }
    setSaving(false);
  };

  const filteredOrders = orders.filter(o => {
    if (filter === "all") return true;
    return o.status === filter;
  });

  const getStatusBadge = (status) => {
    const badges = {
      pendiente: { bg: "bg-amber-100", text: "text-amber-700", icon: Clock, label: "Pendiente" },
      pagado: { bg: "bg-emerald-100", text: "text-emerald-700", icon: CheckCircle2, label: "Pagado" },
      abandonado: { bg: "bg-red-100", text: "text-red-700", icon: AlertCircle, label: "Abandonado" },
      recuperado: { bg: "bg-blue-100", text: "text-blue-700", icon: RefreshCw, label: "Recuperado" }
    };
    const badge = badges[status] || badges.pendiente;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" /> {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-4 sm:space-y-6 max-w-6xl" data-testid="abandoned-orders-page">
        {/* Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-black flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              Recuperación de Pedidos
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Recupera pedidos abandonados automáticamente por WhatsApp
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchData}
              disabled={loading}
              size="sm"
              className="flex-1 sm:flex-none"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Actualizar</span>
            </Button>
            <Button 
              onClick={() => setShowConfig(true)}
              className="bg-amber-600 hover:bg-amber-700 flex-1 sm:flex-none"
              size="sm"
              data-testid="config-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configurar
            </Button>
          </div>
        </div>

        {/* Stats Cards - Responsive grid */}
        {dashboard && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3 sm:grid-cols-4">
            <div className="bg-white border rounded-xl p-3 sm:p-4">
              <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground mb-1">
                <Send className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs">Links Enviados</span>
              </div>
              <p className="text-lg sm:text-2xl font-black">{dashboard.total_links_sent}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">Hoy: {dashboard.today?.sent || 0}</p>
            </div>
            <div className="bg-white border rounded-xl p-3 sm:p-4">
              <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground mb-1">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs">Pendientes</span>
              </div>
              <p className="text-lg sm:text-2xl font-black text-amber-600">{dashboard.links_pending}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">€{dashboard.value_pending?.toFixed(2)}</p>
            </div>
            <div className="bg-white border rounded-xl p-3 sm:p-4">
              <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground mb-1">
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs">Recuperados</span>
              </div>
              <p className="text-lg sm:text-2xl font-black text-blue-600">{dashboard.links_recovered}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">€{dashboard.value_recovered?.toFixed(2)}</p>
            </div>
            <div className="bg-white border rounded-xl p-3 sm:p-4">
              <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground mb-1">
                <Target className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs">Tasa Recuperación</span>
              </div>
              <p className="text-lg sm:text-2xl font-black text-emerald-600">{dashboard.recovery_rate}%</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">Conversión: {dashboard.conversion_rate}%</p>
            </div>
          </div>
        )}

        {/* Recovery Status Banner - Stack on mobile */}
        <div className={`rounded-xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
          dashboard?.recovery_enabled ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"
        }`}>
          <div className="flex items-center gap-2 sm:gap-3">
            {dashboard?.recovery_enabled ? (
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
            )}
            <div>
              <p className={`font-bold text-sm sm:text-base ${dashboard?.recovery_enabled ? "text-emerald-700" : "text-red-700"}`}>
                {dashboard?.recovery_enabled ? "Recuperación Activa" : "Recuperación Desactivada"}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {dashboard?.recovery_enabled 
                  ? `Recordatorios: ${config?.first_reminder_minutes || 5} min y ${config?.second_reminder_minutes || 20} min`
                  : "Activa la recuperación para enviar recordatorios automáticos"
                }
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowConfig(true)}
            className="w-full sm:w-auto"
          >
            {dashboard?.recovery_enabled ? "Configurar" : "Activar"}
          </Button>
        </div>

        {/* Filter Tabs - Horizontal scroll on mobile */}
        <div className="flex gap-2 border-b pb-2 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          {[
            { id: "all", label: "Todos", count: orders.length },
            { id: "pendiente", label: "Pendientes", count: dashboard?.links_pending || 0 },
            { id: "recuperado", label: "Recuperados", count: dashboard?.links_recovered || 0 },
            { id: "abandonado", label: "Abandonados", count: dashboard?.links_abandoned || 0 },
            { id: "pagado", label: "Pagados", count: dashboard?.links_paid || 0 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
                filter === tab.id 
                  ? "bg-amber-100 text-amber-700" 
                  : "text-muted-foreground hover:bg-gray-100"
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Orders List */}
        <div className="bg-white border rounded-xl divide-y">
          {filteredOrders.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hay pedidos {filter !== "all" ? `con estado "${filter}"` : ""}</p>
            </div>
          ) : (
            filteredOrders.map(order => (
              <div key={order.id} className="p-3 sm:p-4 hover:bg-gray-50" data-testid={`order-${order.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium text-sm sm:text-base">{order.customer_phone}</span>
                      {order.customer_name && (
                        <span className="text-muted-foreground text-sm">({order.customer_name})</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                      <span className="flex items-center gap-1">
                        <Euro className="w-3 h-3" />
                        <span className="font-bold">{order.order_total?.toFixed(2)}</span>
                      </span>
                      <span className="text-muted-foreground">
                        {new Date(order.created_at).toLocaleString("es-ES", {
                          day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
                        })}
                      </span>
                      {order.items_summary && (
                        <span className="text-muted-foreground truncate max-w-[120px] sm:max-w-[200px]">
                          {order.items_summary}
                        </span>
                      )}
                    </div>
                    {/* Reminder status */}
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      {order.first_reminder_sent && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full">
                          <Bell className="w-3 h-3" /> 1º enviado
                        </span>
                      )}
                      {order.second_reminder_sent && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-600 rounded-full">
                          <Bell className="w-3 h-3" /> 2º enviado
                        </span>
                      )}
                      {order.recovered && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                          <RefreshCw className="w-3 h-3" /> ¡Recuperado!
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0 self-start sm:self-center">
                    {getStatusBadge(order.status)}
                    
                    {order.status === "pendiente" && (
                      <div className="flex gap-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleSendReminder(order.id)}
                          disabled={saving}
                          title="Enviar recordatorio manual"
                          className="h-8 w-8 p-0"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleMarkPaid(order.id)}
                          disabled={saving}
                          title="Marcar como pagado"
                          className="h-8 w-8 p-0"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Config Modal */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-600" />
              Configuración de Recuperación
            </DialogTitle>
            <DialogDescription>
              Configura los tiempos y mensajes de recordatorio automático
            </DialogDescription>
          </DialogHeader>
          
          {config && (
            <div className="space-y-4 py-4">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Activar Recuperación</p>
                  <p className="text-xs text-muted-foreground">Enviar recordatorios automáticamente</p>
                </div>
                <Switch 
                  checked={config.enabled} 
                  onCheckedChange={v => setConfig(prev => ({ ...prev, enabled: v }))}
                  data-testid="enable-recovery-toggle"
                />
              </div>
              
              {config.enabled && (
                <>
                  {/* Timing */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">1º Recordatorio (minutos)</label>
                      <Input 
                        type="number"
                        min={1}
                        max={60}
                        value={config.first_reminder_minutes}
                        onChange={e => setConfig(prev => ({ ...prev, first_reminder_minutes: parseInt(e.target.value) || 5 }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">2º Recordatorio (minutos)</label>
                      <Input 
                        type="number"
                        min={5}
                        max={120}
                        value={config.second_reminder_minutes}
                        onChange={e => setConfig(prev => ({ ...prev, second_reminder_minutes: parseInt(e.target.value) || 20 }))}
                      />
                    </div>
                  </div>
                  
                  {/* First Message */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Mensaje 1º Recordatorio</label>
                    <Textarea 
                      value={config.first_reminder_message}
                      onChange={e => setConfig(prev => ({ ...prev, first_reminder_message: e.target.value }))}
                      rows={3}
                      placeholder="Usa {link} para insertar el link de pago"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Usa {"{link}"} para insertar el link de pago</p>
                  </div>
                  
                  {/* Second Message */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Mensaje 2º Recordatorio</label>
                    <Textarea 
                      value={config.second_reminder_message}
                      onChange={e => setConfig(prev => ({ ...prev, second_reminder_message: e.target.value }))}
                      rows={3}
                      placeholder="Usa {link} para insertar el link de pago"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button 
              onClick={handleSaveConfig} 
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
